import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import styles from './VolunteerRoleEditPopup.module.css';
import CustomNewButton from 'components/NewDesign/CustomNewButton';
import PropTypes from 'prop-types';
import CloseIcon from 'assets/images/icon_close.svg';
import BackIcon from 'assets/images/icon_arrow_right.svg';
import { addVolunteerRole, editVolunteerRole } from 'api/volunteerApi';
import { toast } from 'react-toastify';
import { getErrorMessage } from 'utils/api';
import {
  getRoleCategoryOptions,
  VOLUNTEER_FIELD_EVENT_OPTIONS,
} from 'config/admin/volunteer';
import CustomDropdown from 'components/NewDesign/CustomDropdown/CustomDropdown';
import useChangeDetection from 'hooks/useChangeDetection';
import CustomNewConfirm from 'components/NewDesign/CustomNewConfirm';
import { TRACK_EVENTS_CODE } from 'constants/eventTypes';
import {
  GROUPING_BASIS,
  groupingBasisOf,
  formatGroupHeading,
  formatGroupRange,
  parseYearRange,
  parseAgeRange,
  groupWords,
} from 'utils/groupLabel';

// Event type options for dropdown. Track Events stores the reserved
// sentinel code (any event with category Track or Training); field
// events store their real event code.
const EVENT_TYPE_OPTIONS = [
  { label: 'All Events', value: null }, // Add blank/all option
  { label: 'Track Events', value: TRACK_EVENTS_CODE },
  ...VOLUNTEER_FIELD_EVENT_OPTIONS.map(({ value, label }) => ({
    label,
    value,
  })),
];

const VolunteerRoleEditPopup = ({
  isOpen,
  onClose,
  clubID,
  role,
  mode = 'edit', // 'edit', 'add'
}) => {
  const [isLoading, setIsLoading] = useState(false);

  // A club that groups by year level enters and sees this role's groups in
  // years ("Y5-Y6"). They are stored in the same "U" form as an age club's
  // ("U10-U11"): the server converts what is typed, and the stored code is
  // shown in years when the role is opened. See utils/groupLabel.js.
  //
  // Wording (Doug, 2026-10-11): each kind of club has its own words. An age
  // club reads Age Group and Age Groups here; a year-level club reads Year
  // Group and Year Groups. They come from groupWords, never from this file.
  // The stored category value stays 'AgeGroupSpecific' for both.
  const { selectedClub } = useSelector((state) => state.user || {});
  const groupingBasis = groupingBasisOf(selectedClub);
  const byYearLevel = groupingBasis === GROUPING_BASIS.YEAR_LEVEL;
  // A club on the Age model types its groups in ages, with no "U" ("10-11").
  const byAge = groupingBasis === GROUPING_BASIS.AGE;
  const words = groupWords(groupingBasis);
  const roleCategoryOptions = getRoleCategoryOptions(words);
  // Examples for the help box in the club's format: U6 and U8, 6 and 8 for an
  // Age club, or Y5 and Y6 (group numbers 10 and 11).
  const exampleGroups = (byYearLevel ? [10, 11] : [6, 8]).map((group) =>
    formatGroupHeading(groupingBasis, group)
  );

  const [formData, setFormData] = useState({
    roleName: '',
    description: '',
    roleCategory: '',
    ageGroupCode: '',
    includeGuests: false,
    hoursEarned: 0,
    numberRequiredDefault: 1,
    eventType: null,
  });

  // Identity lock (volunteer-role-deletion-design.md §14): once a role is
  // referenced by any meet, its category / event type / age groups can no
  // longer change. Repurposing = create a new role, delete the old one.
  const identityLocked = mode === 'edit' && Boolean(role?.isInUse);

  const [errors, setErrors] = useState({});
  const [originalData, setOriginalData] = useState(null);

  const currentData = {
    formData,
  };

  const { showPrompt, confirmLeave, cancelLeave, hasChanges, setShowPrompt } =
    useChangeDetection(originalData, currentData, isOpen);

  // Load data when editing
  useEffect(() => {
    if (mode === 'edit' && role) {
      const initialFormData = {
        roleName: role.roleName || '',
        description: role.description || '',
        roleCategory: role.roleCategory || '',
        ageGroupCode: formatGroupRange(groupingBasis, role.ageGroupCode || ''),
        includeGuests: role.includeGuests || false,
        hoursEarned: role.hoursEarned || 0,
        numberRequiredDefault: role.numberRequiredDefault || 1,
        eventType:
          role.eventTypeMapping && role.eventTypeMapping.length > 0
            ? role.eventTypeMapping[0]
            : null,
      };
      setFormData(initialFormData);
      setOriginalData({
        formData: initialFormData,
      });
    } else {
      const initialFormData = {
        roleName: '',
        description: '',
        roleCategory: '',
        ageGroupCode: '',
        includeGuests: false,
        hoursEarned: 0,
        numberRequiredDefault: 1,
        eventType: null,
      };
      setFormData(initialFormData);
      setOriginalData({
        formData: initialFormData,
      });
    }
    setErrors({});
  }, [mode, role, groupingBasis]);

  const handleInputChange = (name, value) => {
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user starts typing
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: '',
      }));
    }

    // Clear fields based on category selection
    if (name === 'roleCategory') {
      if (value === 'AgeGroupSpecific') {
        // Age group specific: keep both age groups and event type (event type is optional)
        // Don't clear anything
      } else if (value === 'EventSpecific') {
        // Event specific: clear age groups, keep event type
        setFormData((prev) => ({
          ...prev,
          ageGroupCode: '',
        }));
      } else if (value === 'General') {
        // General: clear both age groups and event type
        setFormData((prev) => ({
          ...prev,
          ageGroupCode: '',
          eventType: null,
        }));
      }
    }
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.roleName.trim()) {
      newErrors.roleName = 'Role name is required';
    }

    if (!formData.description.trim()) {
      newErrors.description = 'Description is required';
    }

    if (!formData.roleCategory) {
      newErrors.roleCategory = 'Category is required';
    }

    // Validate hours earned
    if (formData.hoursEarned < 0) {
      newErrors.hoursEarned = 'Hours must be 0 or greater';
    }

    // Validate number required default
    if (formData.numberRequiredDefault < 1) {
      newErrors.numberRequiredDefault = 'Number required must be at least 1';
    }

    // Validate event type for Event Specific roles
    if (formData.roleCategory === 'EventSpecific' && !formData.eventType) {
      newErrors.eventType = 'Event type is required for event-specific roles';
    }

    // Only validate age group code if the category is age group specific
    if (formData.roleCategory === 'AgeGroupSpecific') {
      if (!formData.ageGroupCode.trim()) {
        newErrors.ageGroupCode = `${words.sentence.group} is required for ${words.lower.value}-specific roles`;
      } else if (byYearLevel) {
        // Validate format: years, by the same rule the server applies
        if (parseYearRange(formData.ageGroupCode) === null) {
          newErrors.ageGroupCode =
            'Invalid format. Use years from 0 to 12, like: Y5, Y5-Y6, or Y3, Y5';
        }
      } else if (byAge) {
        // Validate format: ages with no U, by the same rule the server applies
        if (parseAgeRange(formData.ageGroupCode) === null) {
          newErrors.ageGroupCode =
            'Invalid format. Use ages with no U, like: 10, 10-11, or 8, 10';
        }
      } else {
        // Validate format
        const ageGroupPattern =
          /^U\d{1,2}(-U?\d{1,2})?(,\s*U\d{1,2}(-U?\d{1,2})?)*$/i;
        if (!ageGroupPattern.test(formData.ageGroupCode.trim())) {
          newErrors.ageGroupCode =
            'Invalid format. Use formats like: U6, U6-U17, or U6, U7, U9';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    const isAgeGroupSpecific = formData.roleCategory === 'AgeGroupSpecific';

    const editedRole = {
      roleName: formData.roleName.trim(),
      description: formData.description.trim(),
      roleCategory: formData.roleCategory,
      hoursEarned: Number(formData.hoursEarned) || 0,
      numberRequiredDefault: Number(formData.numberRequiredDefault) || 1,
      pointsAwarded: 0, // Keep as 0 for now, not using points
      ageGroupCode:
        formData.roleCategory === 'AgeGroupSpecific'
          ? formData.ageGroupCode.trim()
          : null,
      includeGuests: formData.includeGuests,
      isAgeGroupSpecific: isAgeGroupSpecific,
      eventTypeMapping: formData.eventType ? [formData.eventType] : [],
    };

    // Editing a role and saving it with nothing changed sends nothing: the
    // dialog just closes. hasChanges is the same test that decides whether
    // closing asks about unsaved changes.
    if (mode === 'edit' && !hasChanges()) {
      toast.info('No changes to save.');
      onClose(false);
      return;
    }

    setIsLoading(true);
    try {
      if (mode === 'edit') {
        await editVolunteerRole(clubID, role.volunteerRoleID, editedRole);
      } else {
        await addVolunteerRole(clubID, editedRole);
      }
      setIsLoading(false);
      onClose(true);
    } catch (err) {
      console.error(err);
      toast.error(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = (refresh) => {
    if (hasChanges()) {
      setShowPrompt(true);
      return;
    }
    onClose(refresh);
  };

  const handleConfirmLeave = () => {
    confirmLeave();
    onClose(false);
  };

  if (!isOpen) return null;

  return (
    <>
      <div className="modalOverlay">
        <div className="modal bigModal">
          <div className="modalContent">
            <div className="modalTitle">
              {mode === 'add' ? 'Add Volunteer Role' : 'Edit Volunteer Role'}
            </div>
            <div className={styles.form}>
              <div className={styles.threeColumnRow}>
                <div className={styles.flex2}>
                  <div className={styles.inputGroup}>
                    <label className={styles.label}>Name</label>
                    <input
                      type="text"
                      value={formData.roleName}
                      onChange={(e) =>
                        handleInputChange('roleName', e.target.value)
                      }
                      className={`${styles.input} ${errors.roleName ? styles.inputError : ''}`}
                      disabled={isLoading}
                      placeholder="Enter role name"
                    />
                    {errors.roleName && (
                      <span className={styles.errorText}>
                        {errors.roleName}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.flex1}>
                  <CustomDropdown
                    label="Role Category"
                    name="roleCategory"
                    value={formData.roleCategory}
                    onChange={handleInputChange}
                    options={roleCategoryOptions}
                    error={errors.roleCategory}
                    disabled={isLoading || identityLocked}
                    placeholder="Select category"
                  />
                  {errors.roleCategory && (
                    <span className={styles.errorText}>
                      {errors.roleCategory}
                    </span>
                  )}
                </div>
              </div>

              {identityLocked && (
                <div className={styles.categoryHelp}>
                  <div className={styles.helpHeader}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    <span>
                      This role has been used in {role?.meetUsageCount || 0}{' '}
                      {role?.meetUsageCount === 1 ? 'meet' : 'meets'}
                    </span>
                  </div>
                  <div className={styles.helpContent}>
                    <p>
                      Its category, event type and {words.lower.groups} can no
                      longer be changed. To change what this role covers, create
                      a new role instead.
                    </p>
                  </div>
                </div>
              )}

              <div className={styles.twoColumnRow}>
                <div className={styles.flex1}>
                  <div className={styles.inputGroup}>
                    <label className={styles.label}>Description</label>
                    <input
                      type="text"
                      value={formData.description}
                      onChange={(e) =>
                        handleInputChange('description', e.target.value)
                      }
                      className={`${styles.input} ${errors.description ? styles.inputError : ''}`}
                      disabled={isLoading}
                      placeholder="Enter description"
                    />
                    {errors.description && (
                      <span className={styles.errorText}>
                        {errors.description}
                      </span>
                    )}
                  </div>
                </div>
                {/* "Allow Guest Signup" is not shown: the guests feature is hidden
                    for every club (Doug, 2026-10-11). A role keeps the value it
                    has (includeGuests in formData) and a new role is saved with
                    false. */}
              </div>

              {/* Role Category Help Section */}
              {formData.roleCategory && (
                <div className={styles.categoryHelp}>
                  <div className={styles.helpHeader}>
                    <svg
                      width="16"
                      height="16"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <circle cx="12" cy="12" r="10" />
                      <path d="M12 16v-4" />
                      <path d="M12 8h.01" />
                    </svg>
                    <span>
                      How{' '}
                      {formData.roleCategory === 'AgeGroupSpecific'
                        ? `${words.group} Specific`
                        : formData.roleCategory === 'EventSpecific'
                          ? 'Event Specific'
                          : 'General'}{' '}
                      roles work:
                    </span>
                  </div>
                  <div className={styles.helpContent}>
                    {formData.roleCategory === 'AgeGroupSpecific' && (
                      <>
                        <p>
                          Creates separate roles for each {words.lower.group}{' '}
                          participating in the meet.
                        </p>
                        <ul>
                          <li>
                            <strong>With event type:</strong> Role is specific
                            to that event (e.g., &ldquo;Long Jump Assistant{' '}
                            {exampleGroups[0]}&rdquo;, &ldquo;Long Jump
                            Assistant {exampleGroups[1]}&rdquo;)
                          </li>
                          <li>
                            <strong>All Events:</strong> Role applies to all
                            events for that {words.lower.group} (e.g., &ldquo;
                            {words.value} Marshal {exampleGroups[0]}&rdquo;,
                            &ldquo;{words.value} Marshal {exampleGroups[1]}
                            &rdquo;)
                          </li>
                        </ul>
                      </>
                    )}
                    {formData.roleCategory === 'EventSpecific' && (
                      <>
                        <p>
                          Creates a single role for a specific event type,
                          shared across all {words.lower.groups}.
                        </p>
                        <p>
                          Example: &ldquo;Track Timer&rdquo; for track events -
                          one team handles all {words.lower.values}.
                        </p>
                      </>
                    )}
                    {formData.roleCategory === 'General' && (
                      <>
                        <p>
                          Creates a single general role not tied to{' '}
                          {words.lower.groups} or specific events.
                        </p>
                        <p>
                          Example: &ldquo;Equipment Setup&rdquo;,
                          &ldquo;Registration Desk&rdquo;,
                          &ldquo;Announcer&rdquo;
                        </p>
                      </>
                    )}
                  </div>
                </div>
              )}

              <div className={styles.twoColumnRow}>
                <div className={styles.flex1}>
                  <div className={styles.inputGroup}>
                    <label className={styles.label}>Hours Earned</label>
                    <input
                      type="number"
                      value={formData.hoursEarned}
                      onChange={(e) =>
                        handleInputChange('hoursEarned', e.target.value)
                      }
                      className={`${styles.input} ${errors.hoursEarned ? styles.inputError : ''}`}
                      disabled={isLoading}
                      placeholder="Enter hours (e.g., 1.5)"
                      min="0"
                      step="0.5"
                    />
                    <div className={styles.helpText}>
                      Number of hours volunteers earn for completing this role
                    </div>
                    {errors.hoursEarned && (
                      <span className={styles.errorText}>
                        {errors.hoursEarned}
                      </span>
                    )}
                  </div>
                </div>
                <div className={styles.flex1}>
                  <div className={styles.inputGroup}>
                    <label className={styles.label}>
                      Number Required (default)
                    </label>
                    <input
                      type="number"
                      value={formData.numberRequiredDefault}
                      onChange={(e) =>
                        handleInputChange(
                          'numberRequiredDefault',
                          e.target.value
                        )
                      }
                      className={`${styles.input} ${errors.numberRequiredDefault ? styles.inputError : ''}`}
                      disabled={isLoading}
                      placeholder="Enter number required"
                      min="1"
                      step="1"
                    />
                    <div className={styles.helpText}>
                      Number of volunteers needed for this role. Can be changed
                      for each meet
                    </div>
                    {errors.numberRequiredDefault && (
                      <span className={styles.errorText}>
                        {errors.numberRequiredDefault}
                      </span>
                    )}
                  </div>
                </div>
              </div>

              <div className={styles.twoColumnRow}>
                {formData.roleCategory === 'AgeGroupSpecific' && (
                  <div className={styles.flex1}>
                    <div className={styles.inputGroup}>
                      <label className={styles.label}>{words.groups}</label>
                      <input
                        type="text"
                        value={formData.ageGroupCode}
                        onChange={(e) =>
                          handleInputChange('ageGroupCode', e.target.value)
                        }
                        className={`${styles.input} ${errors.ageGroupCode ? styles.inputError : ''}`}
                        disabled={isLoading || identityLocked}
                        placeholder={
                          byYearLevel
                            ? 'e.g., Y5 or Y5-Y6 or Y3, Y5'
                            : byAge
                            ? 'e.g., 10 or 10-11 or 8, 10'
                            : 'e.g., U6 or U6-U17 or U6, U7, U9'
                        }
                      />
                      {byYearLevel ? (
                        <div className={styles.helpText}>
                          Enter year groups (years 0 to 12) using formats like:
                          <ul className={styles.helpList}>
                            <li>
                              Single year: <strong>Y5</strong>
                            </li>
                            <li>
                              Range: <strong>Y5-Y6</strong>
                            </li>
                            <li>
                              Multiple years: <strong>Y3, Y5</strong>
                            </li>
                          </ul>
                        </div>
                      ) : byAge ? (
                        <div className={styles.helpText}>
                          Enter the ages, with no U, using formats like:
                          <ul className={styles.helpList}>
                            <li>
                              Single age: <strong>10</strong>
                            </li>
                            <li>
                              Range: <strong>10-11</strong>
                            </li>
                            <li>
                              Multiple ages: <strong>8, 10</strong>
                            </li>
                          </ul>
                        </div>
                      ) : (
                        <div className={styles.helpText}>
                          Enter age groups using formats like:
                          <ul className={styles.helpList}>
                            <li>
                              Single age: <strong>U6</strong>
                            </li>
                            <li>
                              Range: <strong>U6-U17</strong> or{' '}
                              <strong>U6-17</strong>
                            </li>
                            <li>
                              Multiple ages: <strong>U6, U7, U9</strong>
                            </li>
                          </ul>
                        </div>
                      )}
                      {errors.ageGroupCode && (
                        <span className={styles.errorText}>
                          {errors.ageGroupCode}
                        </span>
                      )}
                    </div>
                  </div>
                )}
                {formData.roleCategory !== 'General' && (
                  <div className={styles.flex1}>
                    <CustomDropdown
                      label={
                        formData.roleCategory === 'EventSpecific'
                          ? 'Event Type'
                          : 'Event Type (Optional)'
                      }
                      name="eventType"
                      value={formData.eventType}
                      onChange={handleInputChange}
                      options={EVENT_TYPE_OPTIONS}
                      error={errors.eventType}
                      disabled={isLoading || identityLocked}
                      placeholder="Select event type"
                    />
                    {errors.eventType && (
                      <span className={styles.errorText}>
                        {errors.eventType}
                      </span>
                    )}
                  </div>
                )}
              </div>

              <div className={styles.buttons}>
                <CustomNewButton
                  onClick={handleSave}
                  label="Save"
                  large={true}
                  disabled={isLoading}
                />
              </div>
            </div>
            <div className="goBack" onClick={() => handleClose(false)}>
              <div className="goBackImage">
                <img src={BackIcon} alt="back" />
              </div>
              <div className="goBackText">Back</div>
            </div>
            <div className="closeBtn" onClick={() => handleClose(false)}>
              <img src={CloseIcon} alt="close" />
            </div>
          </div>
        </div>
      </div>

      {/* Change Detection Confirmation Dialog */}
      <CustomNewConfirm
        isOpen={showPrompt}
        onClose={cancelLeave}
        title="Discard Changes?"
        description="You have unsaved changes. Are you sure you want to leave without saving?"
        onConfirm={handleConfirmLeave}
        confirmButtonText="Discard Changes"
        confirmButtonVariant="danger"
        onCancel={cancelLeave}
        cancelButtonText="Continue Editing"
      />
    </>
  );
};

VolunteerRoleEditPopup.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  clubID: PropTypes.string.isRequired,
  mode: PropTypes.string,
  role: PropTypes.object,
};

export default VolunteerRoleEditPopup;
